import { HubSpotContact, HubSpotResponse, SignupFormData, Network } from '../types';
import { API_BASE_URL } from '../config/endpoints';

const HUBSPOT_API_ENDPOINT = process.env.REACT_APP_HUBSPOT_ENDPOINT ||
  `${API_BASE_URL}/api/UpdateHubSpotContact`;

// HubSpot Forms API configuration
const HUBSPOT_PORTAL_ID = process.env.REACT_APP_HUBSPOT_PORTAL_ID || '146626206';
const HUBSPOT_FORM_GUID = process.env.REACT_APP_HUBSPOT_FORM_GUID || '356ba6be-6627-44ac-ada4-5c1e0ef34d7f';
const HUBSPOT_FORMS_API_URL = `https://api.hsforms.com/submissions/v3/integration/submit/${HUBSPOT_PORTAL_ID}/${HUBSPOT_FORM_GUID}`;

// Declare global _hsq array for HubSpot client-side tracking
declare global {
  interface Window {
    _hsq: any[];
  }
}

/**
 * Initialize HubSpot tracking queue if not already present
 */
const ensureHubSpotQueue = (): any[] => {
  window._hsq = window._hsq || [];
  return window._hsq;
};

/**
 * Track a virtual page view in HubSpot
 * @param path - The virtual page path (e.g., '/signup/personal-info')
 */
export const trackPageView = (path: string): void => {
  try {
    const _hsq = ensureHubSpotQueue();
    _hsq.push(['setPath', path]);
    _hsq.push(['trackPageView']);

    // Check if HubSpot script is actually loaded
    const hsScriptLoaded = document.getElementById('hs-script-loader') !== null;
    const hsTrackerAvailable = typeof (window as any)._hsq !== 'undefined';

    console.log('📊 HubSpot: Tracked page view:', path);
    console.log('🔍 HubSpot Debug:', {
      scriptElement: hsScriptLoaded,
      trackerQueue: hsTrackerAvailable,
      queueLength: _hsq.length,
      queueSample: _hsq.slice(-3) // Show last 3 items in queue
    });
  } catch (error) {
    console.error('Failed to track HubSpot page view:', error);
  }
};

/**
 * Identify a contact by email in HubSpot
 * This associates all future tracking events with this contact
 * @param email - The contact's email address
 */
export const identifyContact = (email: string): void => {
  try {
    const _hsq = ensureHubSpotQueue();
    _hsq.push(['identify', { email }]);
    _hsq.push(['trackPageView']); // Required to send identify data
    console.log('👤 HubSpot: Identified contact:', email);
  } catch (error) {
    console.error('Failed to identify HubSpot contact:', error);
  }
};

/**
 * Track a custom event in HubSpot
 * @param eventId - Unique identifier for the event
 * @param properties - Optional properties to attach to the event
 */
export const trackEvent = (eventId: string, properties?: Record<string, any>): void => {
  try {
    const _hsq = ensureHubSpotQueue();
    _hsq.push(['trackEvent', {
      id: eventId,
      ...(properties && { value: properties })
    }]);
    console.log('🎯 HubSpot: Tracked event:', eventId, properties);
  } catch (error) {
    console.error('Failed to track HubSpot event:', error);
  }
};

/**
 * Update contact properties in HubSpot via client-side tracking
 * @param properties - Object with contact properties to update
 */
export const updateContactPropertiesTracking = (properties: Record<string, any>): void => {
  try {
    const _hsq = ensureHubSpotQueue();
    _hsq.push(['identify', properties]);
    _hsq.push(['trackPageView']); // Required to send identify data
    console.log('✏️ HubSpot: Updated contact properties via tracking:', properties);
  } catch (error) {
    console.error('Failed to update HubSpot contact properties:', error);
  }
};

/**
 * Check if HubSpot tracking script is available
 * @returns true if HubSpot tracking script is loaded
 */
export const isHubSpotTrackingAvailable = (): boolean => {
  return typeof window !== 'undefined' &&
         (window._hsq !== undefined || document.getElementById('hs-script-loader') !== null);
};

/**
 * Convert form data to HubSpot contact format
 */
const formatHubSpotContact = (
  formData: Partial<SignupFormData>, 
  step: string,
  network?: Network
): HubSpotContact => {
  const baseContact: HubSpotContact = {
    firstname: formData.firstName || '',
    lastname: formData.lastName || '',
    email: formData.email || '',
    signup_step: step,
  };

  // Add optional fields if they exist
  if (formData.phone) baseContact.phone = formData.phone;
  if (formData.companyName) baseContact.company = formData.companyName;
  if (formData.jobTitle) baseContact.jobtitle = formData.jobTitle;
  if (formData.fcaNumber) baseContact.fca_number = formData.fcaNumber;
  if (formData.desiredSubdomain) baseContact.desired_subdomain = formData.desiredSubdomain;
  if (formData.networkIds && formData.networkIds.length > 0) {
    baseContact.network_id = formData.networkIds[0]; // Use first network for legacy compatibility
    baseContact.network_ids = formData.networkIds.join(','); // Store all network IDs
  }
  if (formData.networkNames && formData.networkNames.length > 0) {
    baseContact.network_names = formData.networkNames.join(',');
  }
  if (formData.companiesHouseNumber) baseContact.companies_house_number = formData.companiesHouseNumber;
  if (formData.companySize) baseContact.company_size = formData.companySize;

  // Add network-specific properties
  if (network && network.hubspotSettings?.additionalProperties) {
    Object.assign(baseContact, network.hubspotSettings.additionalProperties);
  }

  // Add timestamp
  baseContact.last_update = new Date().toISOString();
  baseContact.form_version = '2.0';

  return baseContact;
};

/**
 * Create or update a HubSpot contact
 */
export const createOrUpdateHubSpotContact = async (
  formData: Partial<SignupFormData>,
  step: string,
  network?: Network,
  existingContactId?: string
): Promise<HubSpotResponse> => {
  try {
    if (!formData.email) {
      return {
        success: false,
        error: 'Email is required for HubSpot integration'
      };
    }

    const contact = formatHubSpotContact(formData, step, network);
    
    const requestBody = {
      contact,
      contactId: existingContactId,
      pipeline: network?.hubspotSettings?.customPipeline,
      step
    };

    console.log('📨 Sending to HubSpot:', {
      step,
      email: contact.email,
      contactId: existingContactId,
      networkIds: formData.networkIds
    });

    const response = await fetch(HUBSPOT_API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HubSpot API error: ${response.status} - ${errorText}`);
    }

    const result: HubSpotResponse = await response.json();
    
    console.log('✅ HubSpot response:', {
      success: result.success,
      contactId: result.contactId,
      step
    });

    return result;

  } catch (error) {
    console.error('❌ HubSpot integration error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown HubSpot error'
    };
  }
};

/**
 * Send progressive form data to HubSpot as user progresses through steps
 */
export const sendProgressiveHubSpotData = async (
  formData: Partial<SignupFormData>,
  step: string,
  network?: Network,
  existingContactId?: string
): Promise<HubSpotResponse> => {
  // Only send if we have essential data (at minimum email)
  if (!formData.email) {
    console.log('⏭️ Skipping HubSpot update - no email provided yet');
    return {
      success: false,
      error: 'Email required for HubSpot integration'
    };
  }

  try {
    const result = await createOrUpdateHubSpotContact(
      formData,
      step,
      network,
      existingContactId
    );

    if (result.success) {
      console.log(`📈 Progressive HubSpot update successful for step: ${step}`);
    } else {
      console.warn(`⚠️ Progressive HubSpot update failed for step: ${step}`, result.error);
    }

    return result;

  } catch (error) {
    console.error('❌ Progressive HubSpot update error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Send final complete form data to HubSpot
 */
export const sendFinalHubSpotData = async (
  formData: SignupFormData,
  network?: Network,
  existingContactId?: string
): Promise<HubSpotResponse> => {
  return createOrUpdateHubSpotContact(
    formData,
    'form_completed',
    network,
    existingContactId
  );
};

/**
 * Check if HubSpot integration is enabled
 */
export const isHubSpotEnabled = (): boolean => {
  return Boolean(process.env.REACT_APP_HUBSPOT_ENABLED !== 'false');
};

/**
 * Get HubSpot contact by email (for checking existing contacts)
 */
export const getHubSpotContactByEmail = async (email: string): Promise<HubSpotResponse> => {
  try {
    const response = await fetch(`${HUBSPOT_API_ENDPOINT}/contact/${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      if (response.status === 404) {
        return {
          success: false,
          error: 'Contact not found'
        };
      }
      const errorText = await response.text();
      throw new Error(`HubSpot API error: ${response.status} - ${errorText}`);
    }

    const result: HubSpotResponse = await response.json();
    return result;

  } catch (error) {
    console.error('❌ HubSpot contact lookup error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Utility function to determine which steps should trigger HubSpot updates
 */
export const shouldUpdateHubSpot = (step: string): boolean => {
  const hubspotSteps = [
    'personal_details', // First name, last name, email, phone
    'company_details',  // Company name, FCA number
    'platform_details', // Subdomain, network selection
    'form_completed'    // Final submission
  ];

  return hubspotSteps.includes(step);
};

/**
 * Get HubSpot tracking cookie (hutk) for contact association
 */
const getHubSpotTrackingCookie = (): string | null => {
  if (typeof document === 'undefined') return null;

  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith('hubspotutk='));

  return cookie ? cookie.split('=')[1] : null;
};

/**
 * Submit form data to HubSpot Forms API
 * This creates/updates the actual contact record with properties via HubSpot's form submission
 */
export const submitToHubSpotForm = async (
  email: string,
  stepName: string,
  formData: Partial<SignupFormData>
): Promise<void> => {
  try {
    console.log('🔍 HubSpot submission - formData received:', {
      stepName,
      hasEmail: !!formData.email,
      hasFcaNumber: !!formData.fcaNumber,
      hasCompaniesHouseNumber: !!formData.companiesHouseNumber,
      hasDesiredSubdomain: !!formData.desiredSubdomain,
      hasCompanySize: !!formData.companySize,
      hasNetworkNames: !!formData.networkNames,
      networkNamesLength: formData.networkNames?.length || 0
    });

    // Build form fields array for HubSpot Forms API
    const fields: Array<{ name: string; value: string }> = [
      { name: 'email', value: email },
      { name: 'signup_step', value: stepName },
      { name: 'lead_source', value: 'Signup Form' },
    ];

    // Add optional standard fields
    if (formData.firstName) fields.push({ name: 'firstname', value: formData.firstName });
    if (formData.lastName) fields.push({ name: 'lastname', value: formData.lastName });
    if (formData.phone) {
      // Ensure phone number has +44 prefix
      const phone = formData.phone.startsWith('+') ? formData.phone : `+44${formData.phone.replace(/^0/, '')}`;
      fields.push({ name: 'mobilephone', value: phone });
    }
    if (formData.companyName) fields.push({ name: 'company', value: formData.companyName });
    if (formData.jobTitle) fields.push({ name: 'jobtitle', value: formData.jobTitle });

    // Add custom fields (matching HubSpot property names)
    if (formData.fcaNumber) fields.push({ name: 'frn', value: formData.fcaNumber });
    if (formData.icoNumber) fields.push({ name: 'ico_number', value: formData.icoNumber });
    if (formData.companiesHouseNumber) fields.push({ name: 'companies_house_number', value: formData.companiesHouseNumber });
    if (formData.desiredSubdomain) fields.push({ name: 'desired_subdomain', value: formData.desiredSubdomain });
    if (formData.companySize) fields.push({ name: 'company_size', value: formData.companySize });
    if (formData.networkNames && formData.networkNames.length > 0) {
      fields.push({ name: 'network_name', value: formData.networkNames.join(', ') });
    }

    // Add timestamps
    if (stepName === 'contact_info') {
      fields.push({ name: 'signup_started_date', value: new Date().toISOString() });
    } else if (stepName === 'completed') {
      fields.push({ name: 'signup_completed_date', value: new Date().toISOString() });
    }

    // Get tracking cookie for contact association
    const hutk = getHubSpotTrackingCookie();

    // Build request payload
    const payload: any = {
      fields,
      context: {
        pageUri: window.location.href,
        pageName: document.title || 'Signup Form',
      }
    };

    // Include tracking cookie if available (associates submission with existing contact)
    if (hutk) {
      payload.context.hutk = hutk;
    }

    // Log the payload for debugging
    console.log('📤 Submitting to HubSpot:', {
      stepName,
      fields: payload.fields,
      hasHutk: !!hutk
    });

    const response = await fetch(HUBSPOT_FORMS_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    if (response.ok) {
      const responseData = await response.json();
      console.log('✅ HubSpot form submitted successfully:', stepName, responseData);
    } else {
      const errorText = await response.text();
      console.warn('⚠️ HubSpot form submission failed:', response.status, errorText);
    }
  } catch (error) {
    // Log but don't throw - HubSpot failures shouldn't block signup
    console.error('❌ HubSpot form submission error:', error);
  }
};

/**
 * Track step completion with both client-side and server-side HubSpot integration
 * @param stepName - Name of the completed step
 * @param formData - Current form data
 */
export const trackStepCompletionFull = (stepName: string, formData: Partial<SignupFormData>): void => {
  console.log('🎯 trackStepCompletionFull called:', {
    stepName,
    hasEmail: !!formData.email,
    email: formData.email || 'MISSING'
  });

  // Track the step completion event client-side
  trackEvent(`signup_${stepName}_completed`, { step: stepName });

  // Build contact properties based on the step for client-side tracking
  const properties: Record<string, any> = {
    signup_step: stepName,
    // Tag all contacts with source so we know they came from the signup form
    hs_lead_source: 'Signup Form',
    lead_source: 'Signup Form'
  };

  // Submit to HubSpot form to create/update contact record with properties
  if (formData.email) {
    console.log('📧 Email present, calling submitToHubSpotForm');
    submitToHubSpotForm(formData.email, stepName, formData);
  } else {
    console.warn('⚠️ No email in formData, skipping HubSpot submission');
  }

  // Add step-specific properties
  switch (stepName) {
    case 'personal_info':
      if (formData.firstName) properties.firstname = formData.firstName;
      if (formData.lastName) properties.lastname = formData.lastName;
      break;

    case 'contact_info':
      if (formData.email) properties.email = formData.email;
      if (formData.phone) {
        // Ensure phone number has +44 prefix
        properties.mobilephone = formData.phone.startsWith('+') ? formData.phone : `+44${formData.phone.replace(/^0/, '')}`;
      }
      // Identify the contact once we have their email
      if (formData.email) {
        identifyContact(formData.email);
        // Also track the original source when contact is first identified
        properties.original_source = 'Signup Form';
      }
      break;

    case 'fca_number':
      if (formData.fcaNumber) properties.fca_number = formData.fcaNumber;
      if (formData.icoNumber) properties.ico_number = formData.icoNumber;
      break;

    case 'business_info':
      if (formData.companyName) properties.company = formData.companyName;
      if (formData.companiesHouseNumber) properties.companies_house_number = formData.companiesHouseNumber;
      break;

    case 'role_company_size':
      if (formData.jobTitle) properties.jobtitle = formData.jobTitle;
      if (formData.companySize) properties.company_size = formData.companySize;
      break;

    case 'platform_setup':
      if (formData.desiredSubdomain) properties.desired_subdomain = formData.desiredSubdomain;
      if (formData.networkIds && formData.networkIds.length > 0) {
        properties.network_id = formData.networkIds.join(', ');
      }
      if (formData.networkNames && formData.networkNames.length > 0) {
        properties.network_name = formData.networkNames.join(', ');
      }
      break;

    case 'review':
      properties.signup_step = 'review';
      // Track T&Cs acceptance when user clicks submit on review step
      if (formData.acceptTerms) {
        properties.accept_tcs = 'true';
        properties.accept_tcs_date = new Date().toISOString();
      }
      break;

    case 'completed':
      properties.signup_step = 'completed';
      properties.signup_completed_date = new Date().toISOString();
      break;
  }

  // Update contact properties in HubSpot via client-side tracking
  updateContactPropertiesTracking(properties);
};

const hubspotService = {
  // Client-side tracking functions
  trackPageView,
  identifyContact,
  trackEvent,
  updateContactPropertiesTracking,
  isHubSpotTrackingAvailable,
  trackStepCompletionFull,
  // Server-side API functions
  createOrUpdateHubSpotContact,
  sendProgressiveHubSpotData,
  sendFinalHubSpotData,
  isHubSpotEnabled,
  getHubSpotContactByEmail,
  shouldUpdateHubSpot
};

export default hubspotService;
